import { ControlPointState } from "../../constant";
import { SpacerMixMethod } from "libs/constants";
import { patchState, signalStore, withHooks, withMethods, withState } from "@ngrx/signals";
import { inject, effect, untracked } from "@angular/core";
import { ApplicationStateService, ControlPointService2, FinancialsCogsService, UserSettingService, EventHubService, MasterDataService, IfactService } from "../../../../../../../libs/shared";
import { UnitOfMeasureService } from "../../../administrations/services/unit-of-measure.service";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { defaultIfEmpty, Observable, of, pipe, Subscription, forkJoin } from "rxjs";
import { switchMap, catchError, map } from "rxjs/operators";
import { TrademarkModel } from "../../../control-point/models/trademark.model";
import { tapResponse } from "@ngrx/operators";
import { ComponentSaver, ComponentSaverOptions, IValidationErrors, SaveProgress } from "./ngrx-cp1.storage.model";
import { Job, MaterialLabValueModel, IUnitOfMeasure } from "libs/models";

const primaryJobs = [
 "CONDUCTOR CASING", "INTERMEDIATE CASING", "PRODUCTION CASING", "SURFACE CASING",
]

export interface Cp1State {
  trademarks: TrademarkModel[],
  concentrationUoms: { id: string, name: string, order: number }[],
  cogsUoms: IUnitOfMeasure[],
  testTypes: { label: string, value: string }[],
  slurryTypes: { label: string, value: string }[],
  cp1State: ControlPointState,
  loading: boolean;
  error: string | null;
  pumpScheduleValidators: boolean;
  showCOGS: boolean;
  jobId: string;
  groupId: string;
  groupIFactsLabId: number | null;
  isJobWithPlugs: boolean;
  wellboreError: string[];
  editMode: boolean;
  rig: number | null;
  wellId: string | null;
  casingSizeValue: number | null;
  holeSizeValue: number | null;
  jobType: string | null;
  salesOrderNumber: string | null;
  bhp: number | null;
  bhst: number | null;
  bhct: number | null;
  jobMd: number | null;
  jobTvd: number | null;
  group: string | null;
  jobTypeName: string | null;
  customerName: string | null;
  rigName: string | null;
  wellName: string | null;
  plantCode: string | null;
  isPrimaryJob: boolean;
  isPlugJob: boolean;
  isLinerJob: boolean;
  isStageJob: boolean | null;
  labMaterials: MaterialLabValueModel[],
  spacerMixMethod: SpacerMixMethod
}

const initialState: Cp1State = {
  trademarks: null,
  concentrationUoms: null,
  cogsUoms: null,
  testTypes: null,
  slurryTypes: null,
  cp1State: null,
  loading: false,
  error: null,
  pumpScheduleValidators: false,
  showCOGS: false,
  jobId: null,
  groupId: null,
  groupIFactsLabId: null,
  isJobWithPlugs: false,
  editMode: false,
  rig: null,
  wellId: null,
  casingSizeValue: null,
  holeSizeValue: null,
  jobType: null,
  salesOrderNumber: null,
  bhp: null,
  bhst: null,
  bhct: null,
  jobMd: null,
  jobTvd: null,
  group: null,
  jobTypeName: null,
  customerName: null,
  rigName: null,
  wellName: null,
  plantCode: null,
  wellboreError: [],
  isPrimaryJob: false,
  isLinerJob: false,
  isPlugJob: false,
  isStageJob: null,
  labMaterials: [],
  spacerMixMethod: SpacerMixMethod.MixOnTheFly
}

// Module-level variable for lab ID change tracking
let previousLabId: number | null = null;

export const ngrxCP1Store = signalStore(
  withState(initialState),

  withMethods((
    store,
    controlPointService = inject(ControlPointService2),
    unitOfMeasureService = inject(UnitOfMeasureService),
    userSettings = inject(UserSettingService),
    financialService = inject(FinancialsCogsService),
    eventHubService = inject(EventHubService),
    masterDataService = inject(MasterDataService),
    ifactService = inject(IfactService),
    appStateService = inject(ApplicationStateService)
  ) => {
    let trademarks$: Observable<TrademarkModel[]> = null;
    let concentrationUoms$: Observable<{ id: string, name: string, order: number }[]> = null;
    let cogsUoms$: Observable<IUnitOfMeasure[]> = null;
    let testTypes$: Observable<{ label: string, value: string }[]> = null;
    let showCogs: boolean = false;
    let isCogsPermitted: boolean = false;
    let saveComponents: ComponentSaver[] = [];
    let showCOGSSubscription: Subscription;
    let cogsPermitedSubscription: Subscription;
    let groupChangedSubscription: Subscription;
    let spacerMixMethodSubscription: Subscription;
    const checkCogsPermissions = () => {
        cogsPermitedSubscription = financialService.isFinancialGroupVisibleForUser$(store.groupId()).pipe(
              tapResponse({
                next: (isPermitted) => {
                    isCogsPermitted = isPermitted;
                  patchState(store, () => ({ showCOGS: showCogs && isCogsPermitted }));
                  return isPermitted;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            ).subscribe();
      };

    return {
      updateJobValues(job: Job) {
        patchState(store, {
          jobId: job?.id,
          groupId: job?.groupId,
          groupIFactsLabId: job?.groupIFactsLabId,
          isJobWithPlugs: job?.withPlug,
          isPrimaryJob: primaryJobs.includes(job?.jobTypeName?.trim()),
          isLinerJob: job?.isLiner,
          editMode: job?.editMode,
          rig: job?.rig,
          isStageJob: job?.isStageJob,
          wellId: job?.wellId,
          casingSizeValue: job?.casingSizeValue,
          holeSizeValue: job?.holeSizeValue,
          jobType: job?.jobType,
          salesOrderNumber: job?.salesOrderNumber,
          bhp: job?.bhp,
          bhst: job?.bhst,
          bhct: job?.bhct,
          jobMd: job?.jobMd,
          jobTvd: job?.jobTvd,
          group: job?.group,
          jobTypeName: job?.jobTypeName,
          customerName: job?.customerName,
          rigName: job?.rigName,
          wellName: job?.wellName,
          plantCode: job?.plantCode
        });
        checkCogsPermissions();
      },
      updateCPStatus(status: ControlPointState) {
        patchState(store, { cp1State: status });
      },
      loadTrademarks: rxMethod<void>(
        pipe(
          switchMap(() => {
            if (trademarks$ !== null)
              return of(store.trademarks());

            trademarks$ = controlPointService.getTrademarks().pipe(
              tapResponse({
                next: (trademarks) => {
                  patchState(store, { trademarks: trademarks });
                  return trademarks;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            )

            return trademarks$;
          })
        )
      ),
      loadConcentrationUoms: rxMethod<void>(
        pipe(
          switchMap(() => {
            if (concentrationUoms$ !== null && store.concentrationUoms() !== null)
              return of(store.concentrationUoms());

            concentrationUoms$ = controlPointService.getConcentrationUoms().pipe(
              tapResponse({
                next: (uoms) => {
                  patchState(store, { concentrationUoms: uoms });
                  return uoms;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            )

            return concentrationUoms$;
          })
        )
      ),
      loadCOGSUoms: rxMethod<void>(
        pipe(
          switchMap(() => {
            if (cogsUoms$ !== null && store.cogsUoms() !== null)
              return of(store.cogsUoms());

            cogsUoms$ = unitOfMeasureService.getUoM().pipe(
              tapResponse({
                next: (uoms) => {
                  patchState(store, { cogsUoms: uoms });
                  return uoms;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            )

            return cogsUoms$;
          })
        )
      ),
      loadTestTypes: rxMethod<void>(
        pipe(
          switchMap(() => {
            if (testTypes$ !== null && store.testTypes() !== null)
              return of(store.testTypes());

            testTypes$ = masterDataService.listTestTypes().pipe(
              map(res => res.map(itm => ({ label: itm.name, value: itm.id }))),
              tapResponse({
                next: (testTypes) => {
                  patchState(store, { testTypes });
                  return testTypes;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            )

            return testTypes$;
          })
        )
      ),
      loadLabMaterials: rxMethod<void>(
        pipe(
          switchMap(() => {
            const labId = store.groupIFactsLabId();
            if (!labId) {
              patchState(store, { labMaterials: [] });
              return of([]);
            }

            return ifactService.getLabMaterials(labId, null, true).pipe(
              tapResponse({
                next: (labMaterials) => {
                  patchState(store, { labMaterials });
                  return labMaterials;
                },
                error: (ex) => {
                  console.error(ex);
                }
              })
            )
          })
        )
      ),
      initSubscriptions() {
        if (showCOGSSubscription == null) {
            showCOGSSubscription = userSettings.showCogs$.subscribe(show => {
                showCogs = show;
                patchState(store, () => ({ showCOGS: showCogs && isCogsPermitted }));
            });
        }
        if (groupChangedSubscription == null) {
          groupChangedSubscription = eventHubService.onGroupUpdated$.subscribe((group: any) => {
            if (group) {
              patchState(store, { groupIFactsLabId: group.labId })
            }
          });
        }
        if (spacerMixMethodSubscription == null) {
          spacerMixMethodSubscription = appStateService.spacerMixMethodChange$.subscribe((method: SpacerMixMethod) => {
            patchState(store, { spacerMixMethod: method });
          });
        }
      },
      deleteSubscriptions() {
        showCOGSSubscription?.unsubscribe();
        cogsPermitedSubscription?.unsubscribe();
        groupChangedSubscription?.unsubscribe();
        spacerMixMethodSubscription?.unsubscribe();
      },

      registerToSave(
        displayName: string,
        validate: (sp: SaveProgress) => IValidationErrors | null,
        save: (sp: SaveProgress) => Observable<any> | IValidationErrors | null,
        options: ComponentSaverOptions = {}
      ): string {
        const item = new ComponentSaver(displayName, validate, save, options.saveWhenInvalid ?? false);
        saveComponents.push(item);
        return item.id;
      },
      setPumpScheduleValidators(option: boolean){
        patchState(store, { pumpScheduleValidators: option });
      },

      unregisterFromSave(displayName: string) {
        saveComponents = saveComponents.filter(x => x.displayName != displayName);
      },
      populateWellboreError(errors: string[]) {
        if (!errors || errors.length === 0)  return patchState(store, { wellboreError:[] })
        return patchState(store, { wellboreError:errors });
      },

      saveAll(): Observable<SaveProgress> {
        const progress = new SaveProgress(saveComponents);
        progress.state = 'validation';

        // Run validation
        saveComponents.forEach(item => {
          if (item.validate)  {
            const errors = item.validate(progress);
            progress.setValidState(item.id, errors)
          }
        });

        // Collect save operations for valid components only
        const saveObservables: Observable<{ id: string, errors: IValidationErrors }>[] = [];

        saveComponents
          .filter(item => {
            const progressItem = progress.progress.find(p => p.id === item.id);
            return progressItem?.isValid !== false || item.saveWhenInvalid;
          })
          .forEach(item => {
            if (item.save) {
              const result = item.save(progress);

              // Check if result is an Observable
              if (result && typeof (result as Observable<any>).subscribe === 'function') {
                saveObservables.push(
                  (result as Observable<any>).pipe(
                    map(() => ({ id: item.id, errors: null })),
                    defaultIfEmpty({ id: item.id, errors: ['Save completed without a result'] }),
                    catchError((error) => {
                      console.error(`Error saving ${item.displayName}:`, error);
                      return of({ id: item.id, errors: [error.message || 'Unknown error'] });
                    })
                  )
                );
              } else {
                // Handle synchronous return (IValidationErrors)
                saveObservables.push(of({ id: item.id, errors: result as IValidationErrors }));
              }
            }
          });

        // If no observables, return progress immediately
        if (saveObservables.length === 0) {
          progress.state = 'complete';
          return of(progress);
        }

        // Use forkJoin to execute all save operations in parallel
        return forkJoin(saveObservables).pipe(
          map((results) => {
            progress.state = 'inprogress';
            results.forEach(result => {
              progress.setSaveState(result.id, result.errors);
            });
            progress.state = 'complete';
            return progress;
          }),
          catchError((error) => {
            progress.state = 'error';
            console.error('Error in saveAll:', error);
            return of(progress);
          })
        );
      }
    }
  }),

  withHooks({
    onInit(store) {
        store.initSubscriptions();
        store.loadTrademarks();
        store.loadConcentrationUoms();
        store.loadCOGSUoms();
        store.loadTestTypes();
        
        effect(() => {
            const labId = store.groupIFactsLabId();
            if (labId !== previousLabId) {
                previousLabId = labId;
                untracked(() => {
                    store.loadLabMaterials();
                });
            }
        });
    },
    onDestroy(store) {
        store.deleteSubscriptions();
    }
  })
)

